/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { useTranslation } from 'react-i18next';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { Checkbox } from '@/components/forms/checkbox';
import { FormControl, FormField, FormItem } from '@/components/forms/form';
import { useBasket } from '@/providers/basket';

/**
 * Promotional SMS opt-in checkbox rendered alongside the phone field on the
 * shipping-address step. Optional by design — no asterisk, no required
 * validator. Captures opt-in intent only; the server stamps the audit
 * timestamp + source when the box is ticked (see
 * `action.submit-shipping-address.server.ts`).
 *
 * Generic over the parent form so the same component can wire into either
 * the inline shipping-address form (guest path) or the AddressModal form
 * (logged-in add/edit path). The `name` prop defaults to `'smsConsent'`.
 *
 * **Suppression rule.** Returns `null` when CHECKOUT-08's server-computed
 * `basket.c_mattressRecyclingOptInRequired` flag is `true` (CA shoppers
 * with a mattress in cart see the recycling opt-in instead — per HLD-70 we
 * do not stack two distinct consent confirmations in the same step).
 */
type BasketWithMattressFlag = {
    c_mattressRecyclingOptInRequired?: boolean;
};

export interface SmsConsentProps<TFormValues extends FieldValues> {
    /** Parent form's `react-hook-form` control. */
    control: Control<TFormValues>;
    /** Field name in the parent form. Defaults to `'smsConsent'`. */
    name?: Path<TFormValues>;
}

export default function SmsConsent<TFormValues extends FieldValues>({
    control,
    name = 'smsConsent' as Path<TFormValues>,
}: SmsConsentProps<TFormValues>) {
    const basket = useBasket() as (ReturnType<typeof useBasket> & BasketWithMattressFlag) | undefined;
    const { t } = useTranslation('checkout');

    // Suppression: when CHECKOUT-08's <MattressRecyclingOptIn/> is showing
    // (CA shopper + mattress in cart), HLD-70 directs us to omit this block.
    if (basket?.c_mattressRecyclingOptInRequired === true) {
        return null;
    }

    return (
        <FormField
            control={control}
            name={name}
            render={({ field }) => (
                <FormItem data-testid="sms-consent" className="space-y-0">
                    {/* The wrapping <label htmlFor={id}> already supplies the checkbox's accessible
                        name (the visible span text). We deliberately do NOT add `aria-label` on the
                        <Checkbox> — duplicating the label causes screen readers to announce the
                        text twice ("…order status updates… …order status updates…"). */}
                    <label htmlFor={field.name} className="flex cursor-pointer items-start gap-2">
                        <FormControl>
                            {/* mt-0.5 optically centers the size-4 (16px) checkbox with the first
                                line of the leading-5 (20px line-height) label text. Without it the
                                checkbox sits ~2px above the optical center of the first line — most
                                visible on mobile where the label wraps to multiple lines. Matches
                                the pattern used by icon-in-row blocks elsewhere in checkout (e.g.
                                radio-delivery-methods-tile.tsx, ashley-advantage-payment.tsx). */}
                            <Checkbox
                                id={field.name}
                                checked={field.value === true}
                                onCheckedChange={(checked) => field.onChange(checked === true)}
                                className="mt-0.5"
                            />
                        </FormControl>
                        <span className="text-sm leading-5 text-foreground" data-testid="sms-consent-disclosure">
                            {t('smsConsent.optInLabel')}
                        </span>
                    </label>
                </FormItem>
            )}
        />
    );
}
